// @ts-check

/**
 * @file Renders the project task types route.
 */

import _ from "gettext-universal/build/src/translate.js"
import useLocale from "gettext-universal/build/src/use-locale-expo.js"
import {useLocalSearchParams} from "expo-router"
import {memo, useEffect} from "react"
import {ShapeComponent, shapeComponent} from "set-state-compare/build/shape-component.js"
import {View} from "react-native-propforge"
import Button from "../../../src/components/button"
import Card from "../../../src/components/card"
import Modal from "../../../src/components/modal"
import {Project} from "../../../src/frontend-models/project"
import {TaskType} from "../../../src/frontend-models/task-type"
import errorMessage from "../../../src/shared/error-message"
import {optionalRouteParam, requiredRouteParam} from "../../../src/shared/route-params"
import {useScreenLoading} from "../../../src/components/board-layout/screen-loading"
import useThemeColors, {useThemeName} from "../../../src/shared/use-theme-colors"
import Text from "@kaspernj/api-maker/build/utils/text"
import ScreenTitle from "../../../src/components/screen-title"

/** @import {NamedExoticComponent, ReactNode} from "react" */
/** @type {Record<string, object>} */
const styles = {}

/** @typedef {{kind: "error" | "success", text: string} | null} ProjectTaskTypesStatus */
/** @typedef {{defaultForProject: boolean, id: string, name: string}} ProjectTaskTypeRow */

/**
 * @typedef {object} ProjectTaskTypesScreenState
 * @property {string | null} confirmingDeleteId Task type pending delete confirmation.
 * @property {boolean} loading Whether task types are loading.
 * @property {string} projectName Loaded project name.
 * @property {ProjectTaskTypesStatus} status Screen status message.
 * @property {ProjectTaskTypeRow[]} taskTypes Loaded task type rows.
 */

/**
 * Builds a route path while preserving system-test params.
 * @param {Record<string, string | string[]>} params Current route params.
 * @param {string} path Destination path.
 * @returns {string} Path with persisted params.
 */
function routePathWithParams(params, path) {
  const nextPath = new URL(path, "http://127.0.0.1")
  const persistedParamNames = [
    "backendHost",
    "backendPort",
    "backendProtocol",
    "systemTest",
    "systemTestClientWsPort",
    "systemTestScoundrelPort",
    "velocious_transport_url"
  ]
  for (const paramName of persistedParamNames) {
    const value = optionalRouteParam(params, paramName, paramName)
    if (value) {
      nextPath.searchParams.set(paramName, value)
    }
  }
  return `${nextPath.pathname}${nextPath.search}${nextPath.hash}`
}

/** @typedef {{[key: string]: unknown}} ProjectTaskTypesScreenProps */

/**
 * Project task types route screen.
 * @augments {ShapeComponent<ProjectTaskTypesScreenProps, ProjectTaskTypesScreenState>}
 */
class ProjectTaskTypesScreen extends ShapeComponent {
  /** @type {ReturnType<typeof useLocalSearchParams>} */
  params
  /** @type {string} */
  projectID
  /** @type {ReturnType<typeof useThemeColors>} */
  colors = this.hookField()
  /** @type {ReturnType<typeof useThemeName>} */
  themeName = this.hookField()

  /** @type {ProjectTaskTypesScreenState} */
  state = {
    confirmingDeleteId: null,
    loading: true,
    projectName: "",
    status: null,
    taskTypes: []
  }
  loadRequestId = 0
  deletePressHandlers = /** @type {Record<string, () => void>} */ {}

  /** Initializes route params and task type loading. */
  setup() {
    const locale = useLocale()
    useScreenLoading(this.s.loading)
    const params =
      /** @type {Record<string, string | string[]>} */ useLocalSearchParams()
    const projectID = requiredRouteParam(params, "id", "Project ID")
    this.params = params
    this.projectID = projectID
    this.colors = useThemeColors()
    this.themeName = useThemeName()
    useEffect(() => {
      void this.loadData()
    }, [locale, projectID])
  }

  /**
   * Renders the project task types screen.
   * @returns {ReactNode} Project task types content.
   */
  render() {
    const {confirmingDeleteId, loading, projectName, status, taskTypes} =
      this.s
    if (loading) {
      return (
        <View
          style={styles.loadingScreen ||= {flex: 1}}
          testID="projectTaskTypesScreen"
        />
      )
    }
    return (
      <View
        style={styles.screen ||= {
          flex: 1
        }}
        testID="projectTaskTypesScreen"
      >
        <ScreenTitle>
          {_("Task types for %{projectName}", {
            projectName
          })}
        </ScreenTitle>
        <Text
          style={styles[`title-${this.tt.themeName}`] ||= {
            fontSize: 24,
            fontWeight: 700,
            marginBottom: 16
          }}
          testID="projectTaskTypesScreen-title"
        >
          {_("Task types for %{projectName}", {
            projectName
          })}
        </Text>
        {status &&
          <Text
            style={styles[`status-${this.tt.themeName}-${status.kind}`] ||= {
              color: status.kind === "error" ? this.tt.colors.dangerText : this.tt.colors.successText,
              fontWeight: 600,
              marginBottom: 16
            }}
            testID="projectTaskTypesScreen-status"
          >
            {status.text}
          </Text>
        }
        <View
          style={styles.newButtonRow ||= {
            alignSelf: "flex-start",
            marginBottom: 16
          }}
          testID="projectTaskTypesScreen-newButtonRow"
        >
          <Button
            href={routePathWithParams(this.tt.params, `/task-types/new?project_id=${encodeURIComponent(this.tt.projectID)}`)}
            iconName="plus"
            label={_("New task type")}
            testID="projectTaskTypesScreen-newButton"
            variant="neutral"
          />
        </View>
        <Modal
          cardStyle={styles[`confirmModalCard-${this.tt.themeName}`] ||= {
            borderColor: this.tt.colors.danger,
            maxWidth: 420
          }}
          isVisible={Boolean(confirmingDeleteId)}
          onRequestClose={this.tt.onCancelDeletePress}
          testID="projectTaskTypesScreen-deleteConfirm"
        >
          <Text
            style={styles[`confirmTitle-${this.tt.themeName}`] ||= {
              color: this.tt.colors.dangerText,
              fontSize: 16,
              fontWeight: 700,
              marginBottom: 12
            }}
            testID="projectTaskTypesScreen-deleteConfirm-title"
          >
            {_("Are you sure you want to delete this task type?")}
          </Text>
          <View
            style={styles.confirmActions ||= {
              flexDirection: "row",
              gap: 8
            }}
            testID="projectTaskTypesScreen-deleteConfirm-actions"
          >
            <Button
              iconName="xmark"
              label={_("Cancel")}
              onPress={this.tt.onCancelDeletePress}
              testID="projectTaskTypesScreen-deleteConfirm-cancelButton"
              variant="outline"
            />
            <Button
              iconName="trash"
              label={_("Delete")}
              onPress={this.tt.onConfirmDeletePress}
              testID="projectTaskTypesScreen-deleteConfirm-confirmButton"
              variant="destructive"
            />
          </View>
        </Modal>
        {taskTypes.length === 0 &&
          <Text
            style={styles[`emptyText-${this.tt.themeName}`] ||= {
              color: this.tt.colors.textMuted,
              fontSize: 15
            }}
            testID="projectTaskTypesScreen-emptyText"
          >
            {_("No task types found")}
          </Text>
        }
        {taskTypes.map((taskType) => this.renderTaskTypeRow(taskType))}
      </View>
    )
  }

  /**
   * Renders one task type row.
   * @param {ProjectTaskTypeRow} taskType Task type row to render.
   * @returns {ReactNode} Rendered row.
   */
  renderTaskTypeRow(taskType) {
    const taskTypeID = taskType.id
    return (
      <Card
        key={taskTypeID}
        style={styles.rowSpacing ||= {
          alignItems: "center",
          flexDirection: "row",
          justifyContent: "space-between",
          marginBottom: 8
        }}
        testID={`projectTaskTypesScreen-row-${taskTypeID}`}
      >
        <View
          style={styles.rowNameWrap ||= {
            alignItems: "center",
            flex: 1,
            flexDirection: "row",
            gap: 8
          }}
          testID={`projectTaskTypesScreen-row-${taskTypeID}-nameWrap`}
        >
          <Text
            style={styles[`rowName-${this.tt.themeName}`] ||= {
              fontSize: 16,
              fontWeight: 600
            }}
            testID={`projectTaskTypesScreen-row-${taskTypeID}-name`}
          >
            {taskType.name}
          </Text>
          {taskType.defaultForProject &&
            <Text
              style={styles[`defaultBadge-${this.tt.themeName}`] ||= {
                backgroundColor: this.tt.colors.infoBackground,
                borderColor: this.tt.colors.info,
                borderRadius: 999,
                borderWidth: 1,
                color: this.tt.colors.link,
                fontSize: 12,
                fontWeight: 700,
                paddingHorizontal: 8,
                paddingVertical: 3
              }}
              testID={`projectTaskTypesScreen-row-${taskTypeID}-defaultBadge`}
            >
              {_("Default")}
            </Text>
          }
        </View>
        <View
          style={styles.rowActions ||= {
            flexDirection: "row",
            gap: 8
          }}
          testID={`projectTaskTypesScreen-row-${taskTypeID}-actions`}
        >
          <Button
            href={routePathWithParams(this.tt.params, `/task-types/${taskTypeID}/edit`)}
            iconName="pen-to-square"
            label={_("Edit")}
            testID={`projectTaskTypesScreen-row-${taskTypeID}-editButton`}
          />
          <Button
            iconName="trash"
            label={_("Delete")}
            onPress={
              this.deletePressHandlers[taskTypeID] ||= () =>
                this.onDeleteTaskTypePress(taskTypeID)
            }
            testID={`projectTaskTypesScreen-row-${taskTypeID}-deleteButton`}
            variant="destructive"
          />
        </View>
      </Card>
    )
  }

  /**
   * Loads task types for the current project.
   * @returns {Promise<void>} Resolves when task types finish loading.
   */
  loadData = async () => {
    const {projectID} = this.tt
    const requestId = ++this.loadRequestId
    this.s.loading = true
    this.s.status = null
    try {
      const [project, taskTypes] = await Promise.all([
        Project
          .where({
            id: projectID
          })
          .first(),
        TaskType
          .select({
            TaskType: ["defaultForProject", "id", "name", "projectId"]
          })
          .where({
            projectId: projectID
          })
          .order([["name", "asc"], ["id", "asc"]])
          .toArray()
      ])
      if (this.loadRequestId !== requestId) return
      this.s.loading = false
      this.s.projectName = project?.name() || ""
      this.s.taskTypes = taskTypes.map((taskType) => ({
        defaultForProject: Boolean(taskType.defaultForProject()),
        id: `${taskType.id()}`,
        name: taskType.name() || ""
      }))
    } catch (error) {
      if (this.loadRequestId !== requestId) return
      const message = errorMessage(error)
      this.s.loading = false
      this.s.status = {
        kind: "error",
        text:
          message === "Forbidden"
            ? _("Forbidden")
            : `${_("Could not load task types")}: ${message}`
      }
    }
  }

  /** Cancels task type delete confirmation. */
  onCancelDeletePress = () => {
    this.s.confirmingDeleteId = null
  }

  /**
   * Starts task type delete confirmation.
   * @param {string} taskTypeID Task type id to delete.
   * @returns {void} Nothing.
   */
  onDeleteTaskTypePress(taskTypeID) {
    this.s.confirmingDeleteId = taskTypeID
    this.s.status = null
  }

  /**
   * Confirms and deletes the selected task type.
   * @returns {Promise<void>} Resolves when deletion handling completes.
   * @rejects {unknown} When deletion fails.
   */
  onConfirmDeletePress = async () => {
    const {confirmingDeleteId} = this.s
    if (!confirmingDeleteId) {
      throw new Error("Expected a task type id before confirming deletion")
    }
    try {
      const taskTypeToDelete = new TaskType()
      taskTypeToDelete.setId(confirmingDeleteId)
      await taskTypeToDelete.destroy()
      delete this.deletePressHandlers[confirmingDeleteId]
      this.s.confirmingDeleteId = null
      this.s.status = {
        kind: "success",
        text: _("The task type was deleted")
      }
      this.s.taskTypes = this.s.taskTypes.filter(
        (taskType) => taskType.id !== confirmingDeleteId
      )
    } catch (error) {
      this.s.confirmingDeleteId = null
      this.s.status = {
        kind: "error",
        text: errorMessage(error)
      }
      throw error
    }
  }
}

/** @type {NamedExoticComponent<ProjectTaskTypesScreenProps>} */
const ProjectTaskTypesScreenComponent = memo(shapeComponent(ProjectTaskTypesScreen))

export default ProjectTaskTypesScreenComponent
